import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Star, Search, Filter, Edit, Trash2, Check, X, Loader2, Plus } from "lucide-react"
import { useAdminAuth } from "@/context/Admin"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationLink,
  PaginationNext,
} from "@/components/ui/pagination"

export default function ReviewsManagement() {
  const { getAllReviews, createReview, updateReview, deleteReview, toggleReviewApproval } = useAdminAuth()
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterRating, setFilterRating] = useState("all")
  const [filterApproved, setFilterApproved] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 })
  const [editingReview, setEditingReview] = useState(null)
  const [editForm, setEditForm] = useState({ name: "", title: "", review: "", rating: "", date: "" })
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createForm, setCreateForm] = useState({ name: "", title: "", review: "", rating: "5", date: "", avatar: "", is_approved: true })

  useEffect(() => {
    fetchReviews()
  }, [currentPage, filterRating, filterApproved, searchTerm])

  const fetchReviews = async () => {
    setLoading(true)
    try {
      const filters = {}
      if (filterRating !== "all") {
        filters.rating = filterRating
      }
      if (filterApproved !== "all") {
        filters.approved = filterApproved
      }
      if (searchTerm) {
        filters.search = searchTerm
      }
      const data = await getAllReviews(currentPage, 20, filters)
      setReviews(data.reviews || [])
      setPagination(data.pagination || pagination)
    } catch (error) {
      console.error("Error fetching reviews:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (review) => {
    setEditingReview(review)
    setEditForm({
      name: review.name || "",
      title: review.title || "",
      review: review.review || "",
      rating: review.rating?.toString() || "5",
      date: review.date || new Date().toISOString().split("T")[0],
    })
    setIsEditDialogOpen(true)
  }

  const handleSaveEdit = async () => {
    if (!editingReview) return
    try {
      await updateReview(editingReview.id, {
        name: editForm.name,
        title: editForm.title,
        review: editForm.review,
        rating: parseInt(editForm.rating),
        date: editForm.date,
      })
      toast.success("Review updated successfully!")
      setIsEditDialogOpen(false)
      setEditingReview(null)
      fetchReviews()
    } catch (error) {
      console.error("Error updating review:", error)
    }
  }

  const handleDelete = async (reviewId) => {
    if (!confirm("Are you sure you want to delete this review?")) return
    try {
      await deleteReview(reviewId)
      toast.success("Review deleted successfully!")
      fetchReviews()
    } catch (error) {
      console.error("Error deleting review:", error)
    }
  }

  const handleToggleApproval = async (reviewId, currentStatus) => {
    try {
      await toggleReviewApproval(reviewId, !currentStatus)
      toast.success(`Review ${!currentStatus ? "approved" : "unapproved"} successfully!`)
      fetchReviews()
    } catch (error) {
      console.error("Error toggling approval:", error)
    }
  }

  const handleCreate = () => {
    setCreateForm({ name: "", title: "", review: "", rating: "5", date: "", avatar: "", is_approved: true })
    setIsCreateDialogOpen(true)
  }

  const handleSaveCreate = async () => {
    if (!createForm.name || !createForm.title || !createForm.review) {
      toast.error("Please fill in all required fields")
      return
    }
    setIsCreating(true)
    try {
      await createReview({
        name: createForm.name,
        title: createForm.title,
        review: createForm.review,
        rating: parseInt(createForm.rating),
        date: createForm.date || new Date().toISOString().split("T")[0],
        avatar: createForm.avatar || null,
        is_approved: createForm.is_approved
      })
      toast.success("Review created successfully!")
      setIsCreateDialogOpen(false)
      setCreateForm({ name: "", title: "", review: "", rating: "5", date: "", avatar: "", is_approved: true })
      fetchReviews()
    } catch (error) {
      console.error("Error creating review:", error)
    } finally {
      setIsCreating(false)
    }
  }

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Reviews Management</h1>
          <p className="text-gray-600 mt-1">Manage customer reviews</p>
        </div>
        <Button onClick={handleCreate} className="gap-2">
          <Plus className="w-4 h-4" />
          Create Review
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Search reviews..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
            <Select value={filterRating} onValueChange={(value) => {
              setFilterRating(value)
              setCurrentPage(1)
            }}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Filter by Rating" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ratings</SelectItem>
                <SelectItem value="5">5 Stars</SelectItem>
                <SelectItem value="4">4 Stars</SelectItem>
                <SelectItem value="3">3 Stars</SelectItem>
                <SelectItem value="2">2 Stars</SelectItem>
                <SelectItem value="1">1 Star</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterApproved} onValueChange={(value) => {
              setFilterApproved(value)
              setCurrentPage(1)
            }}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="true">Approved</SelectItem>
                <SelectItem value="false">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Reviews List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {reviews.length > 0 ? (
              reviews.map((review) => (
                <Card key={review.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <Avatar>
                        <AvatarImage src={review.avatar || "/placeholder.svg"} alt={review.name} />
                        <AvatarFallback>{review.name?.charAt(0) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h3 className="font-semibold text-lg">{review.name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-4 h-4 ${
                                    i < review.rating
                                      ? "fill-yellow-400 text-yellow-400"
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                              <span className="text-sm text-gray-500 ml-2">
                                {new Date(review.date).toLocaleDateString()}
                              </span>
                              {review.is_approved ? (
                                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                                  Approved
                                </span>
                              ) : (
                                <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                                  Pending
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(review)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleApproval(review.id, review.is_approved)}
                            >
                              {review.is_approved ? (
                                <X className="w-4 h-4 text-red-500" />
                              ) : (
                                <Check className="w-4 h-4 text-green-500" />
                              )}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(review.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <h4 className="font-medium text-gray-900 mb-2">{review.title}</h4>
                        <p className="text-gray-700">{review.review}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="pt-6 text-center text-gray-600">
                  No reviews found matching your criteria.
                </CardContent>
              </Card>
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      if (currentPage > 1) paginate(currentPage - 1)
                    }}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                {[...Array(pagination.totalPages)].map((_, i) => (
                  <PaginationItem key={i}>
                    <PaginationLink
                      href="#"
                      isActive={currentPage === i + 1}
                      onClick={(e) => {
                        e.preventDefault()
                        paginate(i + 1)
                      }}
                      className="cursor-pointer"
                    >
                      {i + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      if (currentPage < pagination.totalPages) paginate(currentPage + 1)
                    }}
                    className={
                      currentPage === pagination.totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Review</DialogTitle>
            <DialogDescription>Update review details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Title</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
            </div>
            <div>
              <Label>Review</Label>
              <Textarea
                value={editForm.review}
                onChange={(e) => setEditForm({ ...editForm, review: e.target.value })}
                rows={5}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rating</Label>
                <Select
                  value={editForm.rating}
                  onValueChange={(value) => setEditForm({ ...editForm, rating: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 Stars</SelectItem>
                    <SelectItem value="4">4 Stars</SelectItem>
                    <SelectItem value="3">3 Stars</SelectItem>
                    <SelectItem value="2">2 Stars</SelectItem>
                    <SelectItem value="1">1 Star</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Review</DialogTitle>
            <DialogDescription>Add a new customer review</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Reviewer name"
              />
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={createForm.title}
                onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                placeholder="Review title"
              />
            </div>
            <div>
              <Label>Review *</Label>
              <Textarea
                value={createForm.review}
                onChange={(e) => setCreateForm({ ...createForm, review: e.target.value })}
                rows={5}
                placeholder="Review content"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rating *</Label>
                <Select
                  value={createForm.rating}
                  onValueChange={(value) => setCreateForm({ ...createForm, rating: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 Stars</SelectItem>
                    <SelectItem value="4">4 Stars</SelectItem>
                    <SelectItem value="3">3 Stars</SelectItem>
                    <SelectItem value="2">2 Stars</SelectItem>
                    <SelectItem value="1">1 Star</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={createForm.date}
                  onChange={(e) => setCreateForm({ ...createForm, date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Avatar URL (optional)</Label>
              <Input
                value={createForm.avatar}
                onChange={(e) => setCreateForm({ ...createForm, avatar: e.target.value })}
                placeholder="https://example.com/avatar.jpg"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_approved"
                checked={createForm.is_approved}
                onChange={(e) => setCreateForm({ ...createForm, is_approved: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="is_approved" className="cursor-pointer">
                Auto-approve review
              </Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setIsCreateDialogOpen(false)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveCreate} disabled={isCreating}>
                {isCreating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Review"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

